import { FlexColumn, FlexRow, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Checkbox } from 'baseui/checkbox';
import { OnChangeParams } from 'baseui/select';
import { LabelSmall, ParagraphXSmall } from 'baseui/typography';
import { useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import {
  ALARMS_REPORTS_PAGE,
  DELIVERY_REPORTS_PAGE,
  DOOR_REPORTS_PAGE,
  EVIDENCES_REPORTS_PAGE,
  PATHS_REPORTS_PAGE,
  ROUTE_REPORTS_PAGE,
  TELEMETRY_REPORTS_PAGE,
} from '../../../constants/app';
import { REPORT_INTERVAL_OPTIONS } from '../../../constants/reports';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import {
  getDeliveryReportFile,
  requestAlarmsByGroupReportFile,
  requestDoorStateByGroupReportFile,
  requestEvidencesByGroupReportFile,
  requestHistoricByGroupReportFile,
  requestPathsByGroupReportFile,
  requestTelemetryByGroupReportFile,
} from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect, pageState } from '../../../storage/app';
import { allGroupsState } from '../../../storage/groups';
import {
  deliveryAllDevicesState,
  evidenceMediaTypeFilterState,
  evidenceOriginFilterState,
  pathDistanceFilterState,
  pathTimeFilterState,
  reportAlarmFilterState,
  reportEndDateState,
  reportFilterGroupState,
  reportIntervalState,
  reportIsManualIntervalState,
  reportMaxEndDateState,
  reportMinStartDateState,
  reportSearchTypeState,
  reportSelectedAssetIdsState,
  reportStartDateState,
  reportViewState,
} from '../../../storage/reports';
import { AssetPicker } from '../../shared/AssetPicker';
import { EnhancedLabeledSelect } from '../../ui/EnhancedLabeledSelect';
import { LabeledSelect } from '../../ui/LabeledSelect';
import { StyledDateTimeRangePicker } from '../../ui/StyledDateTimeRangePicker';
import { StyledTooltip } from '../../ui/StyledTooltip';

type Props = Readonly<{
  withTimeInterval: boolean;
  extraFilters?: React.ReactNode;
  extraManualFilters?: React.ReactNode;
  manualIntervalLabel?: string;
}>;

function SharedReportFilters({
  withTimeInterval,
  extraFilters,
  extraManualFilters,
  manualIntervalLabel = 'Intervalo Manual',
}: Props) {
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { theme, css, classes } = useStyles();
  const [datesError, setDatesError] = useState('');
  const [endDate, setEndDate] = useRecoilState(reportEndDateState);
  const [filterGroup, setFilterGroup] = useRecoilState(reportFilterGroupState);
  const [interval, setInterval] = useRecoilState(reportIntervalState);
  const [isManualInterval, setIsManualInterval] = useRecoilState(reportIsManualIntervalState);
  const [searchType, setSearchType] = useRecoilState(reportSearchTypeState);
  const [selectedAssetIds, setSelectedAssetIds] = useRecoilState(reportSelectedAssetIdsState);
  const [startDate, setStartDate] = useRecoilState(reportStartDateState);
  const [viewType, setViewType] = useRecoilState(reportViewState);
  const apiParams = useRecoilValue(apiParamsState);
  const deliveryAllDevices = useRecoilValue(deliveryAllDevicesState);
  const groups = useRecoilValue(allGroupsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const maxEndDate = useRecoilValue(reportMaxEndDateState);
  const minStartDate = useRecoilValue(reportMinStartDateState);
  const page = useRecoilValue(pageState);
  const resetFilterGroup = useResetRecoilState(reportFilterGroupState);
  const evidenceMediaType = useRecoilValue(evidenceMediaTypeFilterState);
  const evidenceOrigin = useRecoilValue(evidenceOriginFilterState);
  const reportAlarmType = useRecoilValue(reportAlarmFilterState);
  const pathTimeFilter = useRecoilValue(pathTimeFilterState);
  const pathDistanceFilter = useRecoilValue(pathDistanceFilterState);

  const groupOptions = useMemo(() => {
    if (!groups) return;
    const options: { id: string | undefined; label: string; assetIds?: any }[] = groups.map((group) => ({
      id: group._id,
      label: group.name,
      assetIds: group.assignedAssets,
    }));
    return options;
  }, [groups]);

  const onGroupChange = (params: OnChangeParams) => {
    params.option?.id ? setFilterGroup(params.option.id as string) : resetFilterGroup();
  };

  const createReport = async () => {
    if (!apiParams || !startDate || !endDate || !filterGroup) return;
    startLoading();
    let promise: typeof requestEvidencesByGroupReportFile | undefined = undefined;
    let filters: { [key: string]: string | number } | undefined = undefined;
    if (page === PATHS_REPORTS_PAGE.id) {
      promise = requestPathsByGroupReportFile;
      filters = {
        minSeconds: pathTimeFilter * 60 || 300,
        minMeters: pathDistanceFilter || 700,
      };
    }
    if (page === ROUTE_REPORTS_PAGE.id) {
      promise = requestHistoricByGroupReportFile;
    }
    if (page === TELEMETRY_REPORTS_PAGE.id) {
      promise = requestTelemetryByGroupReportFile;
    }
    if (page === DOOR_REPORTS_PAGE.id) {
      promise = requestDoorStateByGroupReportFile;
    }
    if (page === ALARMS_REPORTS_PAGE.id) {
      promise = requestAlarmsByGroupReportFile;
      if (reportAlarmType !== undefined) {
        filters = {};
        filters['alarmType'] = reportAlarmType;
      }
    }
    if (page === EVIDENCES_REPORTS_PAGE.id) {
      promise = requestEvidencesByGroupReportFile;
      if (evidenceMediaType || evidenceOrigin) {
        filters = {};
        if (evidenceMediaType) {
          filters['mediaType'] = evidenceMediaType;
        }
        if (evidenceOrigin) {
          filters['origin'] = evidenceOrigin;
        }
      }
    }
    if (promise) {
      const { error } = await promise(
        filterGroup,
        startDate,
        endDate,
        apiParams.clientId,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
        filters,
      );
      if (!error) {
        showPositiveFeedback('Tu reporte será procesado, visita la sección de descargas para obtenerlo.');
      } else if (error) {
        showFailFeedback(`${error}`);
      }
    }
    stopLoading();
  };

  const downloadDeliveryReport = async () => {
    if (!apiParams || !deliveryAllDevices) return;
    startLoading();
    const { url, error } = await getDeliveryReportFile(
      deliveryAllDevices._id,
      apiParams.clientId,
      isAllWorkspacesSelected ? undefined : apiParams.subClientId,
    );
    if (!error && url) {
      window.open(url, '_blank');
    } else {
      showFailFeedback(error || 'Ocurrió un error al descargar el reporte, inténtalo más tarde.');
    }
    stopLoading();
  };

  return (
    <>
      <FlexRow
        classNames={css({
          flexWrap: 'wrap',
        })}
      >
        {!isAllWorkspacesSelected && (
          <FlexColumn gap={theme.sizing.scale200}>
            <LabelSmall>Buscar por:</LabelSmall>
            <ButtonGroup
              size="compact"
              mode="radio"
              selected={searchType}
              onClick={(event, index) => {
                setSearchType(index);
              }}
            >
              <Button>Vehículos</Button>
              <Button>Grupos</Button>
            </ButtonGroup>
          </FlexColumn>
        )}
        {searchType === 0 && (
          <AssetPicker
            onChange={(asset) => setSelectedAssetIds([asset._id])}
            onClear={() => setSelectedAssetIds([])}
            assetId={selectedAssetIds?.length ? selectedAssetIds[0] : undefined}
            maxWidth={366}
          />
        )}
        {searchType === 1 && (
          <LabeledSelect
            label="Selecciona un grupo:"
            options={groupOptions}
            value={filterGroup ? [{ id: filterGroup }] : undefined}
            onChange={onGroupChange}
            maxWidth={366}
          />
        )}
      </FlexRow>
      {extraFilters}
      {(withTimeInterval || (searchType === 1 && filterGroup)) && (
        <FlexColumn>
          {withTimeInterval && (
            <>
              <Checkbox
                checked={isManualInterval}
                checkmarkType="toggle"
                onChange={(e) => setIsManualInterval(e.target.checked)}
                labelPlacement="right"
              >
                {manualIntervalLabel}
              </Checkbox>
              <FlexRow>
                {!isManualInterval && (
                  <EnhancedLabeledSelect
                    label="Intervalo de tiempo:"
                    onChange={(params) => {
                      setInterval((params.option?.id as number) || 0);
                    }}
                    value={[{ id: interval }]}
                    options={REPORT_INTERVAL_OPTIONS}
                    disabled={isManualInterval}
                    maxWidth={160}
                    inline
                    inset
                  />
                )}
                {isManualInterval && (
                  <>
                    <StyledDateTimeRangePicker
                      todayIsMaxDate={false}
                      dates={[startDate ? new Date(startDate) : undefined, endDate ? new Date(endDate) : undefined]}
                      onDatesChange={(dates) => {
                        if (Array.isArray(dates)) {
                          if (dates.length > 1 && dates[0] && dates[1]) {
                            setStartDate(dates[0].getTime());
                            setEndDate(dates[1].getTime());
                          } else if (dates[0]) {
                            setStartDate(dates[0].getTime());
                            setEndDate(null);
                          }
                        }
                      }}
                      onError={setDatesError}
                      startLabel="Fecha y hora de inicio:"
                      endLabel="Fecha y hora fin:"
                      maxDayInterval={7}
                      minStartDate={minStartDate}
                      maxEndDate={maxEndDate}
                    />
                    {datesError && (
                      <ParagraphXSmall
                        margin={0}
                        color={theme.colors.negative}
                      >
                        {datesError}
                      </ParagraphXSmall>
                    )}
                  </>
                )}
              </FlexRow>
              {extraManualFilters}
            </>
          )}
          {searchType === 1 && filterGroup && (
            <FlexRow classNames={classes.centeredBetween}>
              <ButtonGroup
                size="compact"
                mode="radio"
                selected={viewType}
                onClick={(_, index) => {
                  setViewType(index);
                }}
              >
                <Button>
                  <StyledTooltip
                    content="Tabla"
                    ignoreBoundary
                    placement="top"
                    popoverMargin={15}
                    showArrow={false}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={theme.sizing.scale650}
                      viewBox="0 -960 960 960"
                      width={theme.sizing.scale650}
                      fill={viewType === 0 ? theme.colors.contentInversePrimary : theme.colors.contentPrimary}
                    >
                      <path d="M320-80q-33 0-56.5-23.5T240-160v-480q0-33 23.5-56.5T320-720h480q33 0 56.5 23.5T880-640v480q0 33-23.5 56.5T800-80H320Zm0-80h200v-120H320v120Zm280 0h200v-120H600v120ZM80-240v-560q0-33 23.5-56.5T160-880h560v80H160v560H80Zm240-120h200v-120H320v120Zm280 0h200v-120H600v120ZM320-560h480v-80H320v80Z" />
                    </svg>
                  </StyledTooltip>
                </Button>
                <Button>
                  <StyledTooltip
                    content="Lista"
                    ignoreBoundary
                    placement="top"
                    popoverMargin={15}
                    showArrow={false}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height={theme.sizing.scale650}
                      viewBox="0 -960 960 960"
                      width={theme.sizing.scale650}
                      fill={viewType === 1 ? theme.colors.contentInversePrimary : theme.colors.contentPrimary}
                    >
                      <path d="M360-240h440v-107H360v107ZM160-613h120v-107H160v107Zm0 187h120v-107H160v107Zm0 186h120v-107H160v107Zm200-186h440v-107H360v107Zm0-187h440v-107H360v107ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Z" />
                    </svg>
                  </StyledTooltip>
                </Button>
              </ButtonGroup>
              {page !== DELIVERY_REPORTS_PAGE.id && (
                <StyledTooltip
                  content="Descargar por grupo"
                  showArrow={false}
                  placement="top"
                  ignoreBoundary
                >
                  <Button
                    size="compact"
                    shape="circle"
                    kind="tertiary"
                    isLoading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      createReport();
                    }}
                  >
                    <MaterialIcon
                      name="download"
                      size="compact"
                    />
                  </Button>
                </StyledTooltip>
              )}
              {page === DELIVERY_REPORTS_PAGE.id && deliveryAllDevices && (
                <StyledTooltip
                  content="Descargar por grupo"
                  showArrow={false}
                  placement="top"
                  ignoreBoundary
                >
                  <Button
                    size="compact"
                    shape="circle"
                    kind="tertiary"
                    isLoading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadDeliveryReport();
                    }}
                  >
                    <MaterialIcon
                      name="download_for_offline"
                      size="default"
                    />
                  </Button>
                </StyledTooltip>
              )}
            </FlexRow>
          )}
        </FlexColumn>
      )}
    </>
  );
}

export default SharedReportFilters;
